import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { CODRequest } from "../api/types";
import { celebrate } from "../confetti";
import { toast } from "./Kit";
import { burstFrom } from "./SaleDialog";
import { ShellIcon } from "./ShellIcons";

const rm = (sen: number) => "RM" + (sen / 100).toFixed(2);

/** The address in one line, as cp's form takes it. */
export const codAddress = (cod: CODRequest) =>
  [cod.address_line1, cod.address_line2, [cod.postcode, cod.city].filter(Boolean).join(" "), cod.state].filter(Boolean).join(", ");

/**
 * 已开单: placed in cp, said in gold with a burst from the button pressed (prototype chat.js dealAct "cod"). The bar
 * over the chat and 客户详情's 收单 card both close it. Throws the server's words when it did not take.
 */
export async function codPlaced(waID: string, cod: CODRequest) {
  const from = burstFrom();
  try {
    await api.codDone(waID);
  } catch (e) {
    throw new Error(e instanceof ApiError ? e.message : "没改到，再试一次");
  }
  toast(`开单了 · ${rm(cod.grand_total_sen)}`, "gold");
  celebrate("small", from ?? { x: 0.62, y: 0.3 });
}

/**
 * 货到付款待开单: a cash-on-delivery order the customer confirmed in chat,
 * with what the colleague types into cp — the details as the shop cleaned
 * them and the shop's total, which is what the customer was told. It sits
 * where the hand-off alert sits and stays until somebody presses 已开单.
 *
 * The bot cannot create a cash-on-delivery order; the owner wants those
 * placed by hand (2026-10-01), and the customer was told only that the parcel
 * will come with a tracking number — never 转人工.
 */
export default function CODCard({
  waID,
  cod,
  mayClose,
  onClosed,
}: {
  waID: string;
  cod: CODRequest;
  mayClose: boolean;
  onClosed: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const address = codAddress(cod);
  const items = cod.items.map((i) => `${i.name || i.slug} x ${i.quantity}`).join("、");
  // In the order cp's form asks for them.
  const text = [cod.name, cod.phone, address, items, `货到付款 ${rm(cod.grand_total_sen)}`].join("\n");

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast("复制了：名字、电话、地址、货、金额");
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setErr("复制不了，请手动选取上面的资料");
    }
  }

  async function close() {
    setBusy(true);
    setErr("");
    try {
      await codPlaced(waID, cod);
      onClosed();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wc-alert cod">
      <ShellIcon name="truck" />
      <span className="wc-cod">
        <b>
          货到付款待开单 · {rm(cod.grand_total_sen)}
          {cod.shipping_fee_sen > 0 ? `（含运费 ${rm(cod.shipping_fee_sen)}）` : ""}
        </b>
        <span className="wc-cod-2">
          <span>{items}</span>
          <span>
            {cod.name} · {cod.phone}
          </span>
          <span>{address}</span>
        </span>
        {err && <em className="wc-alert-why">{err}</em>}
      </span>
      <button type="button" className={`quick ghost${copied ? " copied" : ""}`} onClick={() => void copy()}>
        <ShellIcon name={copied ? "check" : "copy"} size={15} />
        {copied ? "已复制" : "复制资料"}
      </button>
      {mayClose && (
        <button type="button" className="quick primary" disabled={busy} onClick={() => void close()}>
          <ShellIcon name="check" size={15} />
          已开单
        </button>
      )}
    </div>
  );
}
